import Skeleton from '@cvent/carina/components/Skeleton';
import {Card} from '../card/Card';
import {useSkeletonDelay} from './useSkeletonDelay';

interface PlanSkeletonProps {
  /**
   * Number of plan cards to show
   * Planner: 4 cards (Free, Standard, Pro, Premium)
   * Supplier: 3 cards (Free, Standard, Pro)
   */
  cardCount?: 3 | 4;
  /**
   * Skip the 300ms delay and show skeleton immediately
   * Use this for data-loading states after Suspense to prevent remount flicker
   */
  skipDelay?: boolean;
}

/**
 * Skeleton for plan selection pages (SelectPlans, ReactivatePlan)
 * Shows plan cards layout + feature comparison
 * Includes a 300ms delay to prevent flash on fast-loading pages (unless skipDelay is true)
 */
export const PlanSkeleton = ({
  cardCount = 4,
  skipDelay = false,
}: PlanSkeletonProps) => {
  const show = useSkeletonDelay(skipDelay);

  if (!show) {
    return null;
  }

  return (
    <div
      className="flex justify-center items-start md:[margin-top:var(--carina-spacing-12)]"
      data-cvent-id="plan-skeleton"
    >
      <Card styleClasses="p-4 pt-0 px-6 sm:p-8 sm:pl-6 md:pl-8 max-w-[1516px] mb-8">
        {/* Back button skeleton */}
        <div className="[margin-bottom:var(--carina-spacing-8)]">
          <Skeleton className="w-[80px] h-[32px] rounded" hasShimmer />
        </div>

        {/* Header with title and billing toggle */}
        <div className="flex justify-between items-center mb-8">
          <Skeleton className="w-[250px] h-[28px] rounded" hasShimmer />
          <Skeleton className="w-[200px] h-[40px] rounded-full" hasShimmer />
        </div>

        {/* Plan cards skeleton */}
        <div className="flex flex-wrap justify-start items-end gap-4">
          {Array.from({length: cardCount}).map((_, index) => (
            <Skeleton
              key={index}
              className="h-[295px] w-[335px] rounded-lg"
              hasShimmer
            />
          ))}
        </div>

        {/* Note text skeleton */}
        <div className="pt-2 pb-8">
          <Skeleton className="w-[90%] h-[14px] rounded" hasShimmer />
        </div>

        {/* Feature comparison collapsible header */}
        <div className="mt-4">
          <Skeleton className="w-[200px] h-[24px] rounded" hasShimmer />
        </div>
      </Card>
    </div>
  );
};
